import React, { useEffect, useState, useRef, lazy, createElement } from 'react';
import { useRevealAnimation } from '../utils/IntersectionObserverUtil';
export function Hero() {
  // Use centralized reveal animation
  useRevealAnimation();
  // Reference for the video container
  const videoRef = useRef<HTMLDivElement>(null);
  // State to prevent re-initialization after first load
  const [videoLoaded, setVideoLoaded] = useState(false);
  useEffect(() => {
    if (!videoRef.current) return;
    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };
    const handleIntersection = (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && !videoLoaded) {
        // Create video element
        const video = document.createElement('video');
        video.src = 'https://cdn.shopify.com/videos/c/o/v/c239c42522814b32af3cf5206bdab235.mp4#t=5';
        video.className = 'absolute top-0 left-0 w-full h-full object-cover rounded-lg';
        video.controls = true;
        video.preload = 'metadata';
        video.playsInline = true;
        if (videoRef.current) {
          // Append video
          videoRef.current.appendChild(video);
          // Create overlay play button
          const playButton = document.createElement('div');
          playButton.className = 'absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 cursor-pointer rounded-lg z-10';
          const playIcon = document.createElement('div');
          playIcon.className = 'bg-white bg-opacity-80 text-teal-600 rounded-full p-3 shadow-lg';
          playIcon.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-play"><polygon points="5 3 19 12 5 21 5 3"></polygon></svg>`;
          playButton.appendChild(playIcon);
          videoRef.current.appendChild(playButton);
          // Add click handler to play video from beginning
          playButton.addEventListener('click', () => {
            if (!videoRef.current) return;
            playButton.style.display = 'none';
            video.currentTime = 0;
            video.play();
          });
          // Set flag to prevent re-loading
          setVideoLoaded(true);
          // Disconnect observer after loading
          observer.disconnect();
        }
      }
    };
    const observer = new IntersectionObserver(handleIntersection, options);
    observer.observe(videoRef.current);
    return () => {
      observer.disconnect();
    };
  }, [videoLoaded]);
  return <div className="w-full bg-gradient-to-r from-blue-50 to-teal-50 py-8 md:py-12 lg:py-16 xl:py-20 gradient-animation" data-id="element-388">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 md:px-8 lg:px-8" data-id="element-389">
        <div className="flex flex-col items-center gap-4 md:gap-5 lg:gap-6 xl:gap-8" data-id="element-390">
          {/* Headlines Section - optimized for mobile first */}
          <div className="text-left w-full max-w-4xl space-y-2 sm:space-y-3 md:space-y-3.5 lg:space-y-4" data-id="element-391">
            <h1 className="text-3xl sm:text-4xl md:text-5xl md:font-extrabold lg:text-5xl xl:text-6xl font-extrabold text-gray-900 leading-tight" data-id="element-392">
              If You Use Eye Drops,
            </h1>
            <h1 className="text-3xl sm:text-4xl md:text-5xl md:font-extrabold lg:text-5xl xl:text-6xl font-extrabold text-teal-600 leading-tight mb-4 sm:mb-5 md:mb-4 lg:mb-6" data-id="element-393">
              You Need to Read This.
            </h1>
          </div>
          {/* Image Section - optimized for tablet and desktop */}
          <div className="w-full max-w-2xl md:max-w-2xl lg:max-w-3xl mx-auto reveal-on-scroll mt-3 md:mt-4 lg:mt-6" data-id="element-394">
            <div className="rounded-lg overflow-hidden border border-gray-200 shadow-lg bg-white" data-id="element-395">
              <img src="https://cdn.shopify.com/s/files/1/0679/7135/0570/files/unnamed_18.jpg?v=17611193181" alt="Comparison showing eye drops with a red cross (ineffective) versus heat therapy with a green check (effective solution)" className="w-full h-auto object-contain" width="800" height="450" fetchpriority="high" decoding="async" onError={e => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://placehold.co/600x400/e2e8f0/64748b?text=Eye+Treatment+Comparison';
            }} data-id="element-396" />
            </div>
          </div>
          {/* Author Section - optimized for tablet */}
          <div className="text-center max-w-4xl w-full" data-id="element-397">
            <div className="flex items-center justify-center mb-5 md:mb-6 lg:mb-8 reveal-on-scroll" data-id="element-398">
              <div className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full bg-gray-200 mr-3 md:mr-4 overflow-hidden hover-zoom flex-shrink-0" data-id="element-399">
                <img src="https://cdn.shopify.com/s/files/1/0679/7135/0570/files/Gemini_Generated_Image_1i68bd1i68bd1i68.png?v=1761116494" alt="Dr. Jennifer Martinez" className="w-full h-full object-cover" width="56" height="56" loading="lazy" data-id="element-400" />
              </div>
              <div className="text-left" data-id="element-401">
                <p className="font-medium text-gray-900 text-base md:text-lg lg:text-xl" data-id="element-402">
                  Dr. Linda Matthews
                </p>
                <p className="text-sm md:text-base lg:text-lg text-gray-600" data-id="element-403">
                  Board Certified Dry-Eye Expert
                </p>
                <p className="text-sm md:text-base lg:text-lg text-gray-600" data-id="element-404">
                  Updated April 7th 2025
                </p>
              </div>
            </div>
            {/* Content - optimized for tablet */}
          <div className="bg-gradient-to-b from-white to-[#f9fafb] rounded-2xl p-8 shadow-lg border border-gray-100 max-w-2xl mx-auto leading-relaxed text-gray-800" data-id="element-405">
        <h4 className="text-xl md:text-1xl font-bold text-gray-900 mb-2" data-id="element-406">
       This is shocking but true for soo many women…
      </h4>
  <p className="mb-4" data-id="element-407">
    The last time you tried to put on mascara, your eyes were stinging before you even left the bathroom.
  </p>
            <p className="mb-4" data-id="element-408">
    You tried to hold it in, hoping the burning would pass. You’d already spent twenty minutes getting ready.
  </p>
 <p className="mb-4" data-id="element-409">
    But the sting grew sharper... until the tears came anyway.
  </p>
  <p className="mb-4" data-id="element-410">
    Not tears of joy. Not emotion.
  </p>

  <p className="mb-4" data-id="element-411">
    Your eyes were reacting to the very thing you've been using to <strong data-id="element-412">"fix"</strong> them.
  </p>

  <p className="mb-4" data-id="element-413">
    Those <strong className="text-teal-600" data-id="element-414">eye drops,</strong> the ones your doctor said were safe, the ones the CVS pharmacist recommended,
    the ones you've been buying every month for the last three years?
  </p>

  <p className="mb-4" data-id="element-415">
    They're <strong data-id="element-416">destroying the microscopic oil glands</strong> in each eyelid that used to keep your eyes comfortable all day.
    Without them, no amount of moisture stays put. And once those glands shut down completely, <strong data-id="element-417">they never regenerate.</strong>
  </p>

  <p className="mb-4 font-semibold text-teal-600" data-id="element-418">Here's what they never told you:</p>


  <p className="mb-4" data-id="element-419">
    In <strong data-id="element-420">1974</strong>, pharmaceutical companies developed artificial tears to treat “dry eye syndrome.” 
    They believed the problem was insufficient tear production. More liquid = problem solved.
  </p>

  <p className="mb-4 font-semibold text-teal-600" data-id="element-421">They were wrong.</p>

  <p className="mb-4" data-id="element-422">
    By <strong data-id="element-423">2011</strong>, researchers confirmed that <strong data-id="element-424">86% of chronic dry eye cases</strong> aren’t caused by lack of tears. They’re caused by
    <strong data-id="element-425"> Meibomian Gland Dysfunction, (MGD)</strong> when the tiny oil glands along your eyelid margin clog, harden, and eventually stop working.
  </p>

  <p className="mb-4" data-id="element-426">
    These glands produce the <strong data-id="element-427">lipid layer</strong> that seals moisture into your tear film. Without that protective oil,
    <span className="text-teal-600 font-semibold" data-id="element-428"> tears evaporate in seconds</span> no matter how many drops you add.
  </p>

  <p className="mb-4 font-semibold text-teal-600" data-id="element-429"></p>

  <p className="mb-4" data-id="element-430">
    The Tear Film &amp; Ocular Surface Society issued updated treatment guidelines in <strong data-id="element-431">2017</strong>.
    Over <strong data-id="element-432">40 peer-reviewed studies</strong> confirmed that artificial tears don’t address clogged oil glands.
    In fact, a <strong data-id="element-433">2019 clinical review</strong> found that patients using drops 4+ times daily lost functioning glands <strong data-id="element-434">3.2× faster </strong>
    than those who used nothing at all.
  </p>

  <p className="mb-4 text-teal-600 font-semibold" data-id="element-435">Not slower. Faster.</p>

  <p className="mb-4" data-id="element-436">
    Why? Because every time those drops evaporate (which happens in minutes), they carry away whatever natural oils your struggling glands managed to produce.
    Your brain interprets this as “the glands aren’t needed because we’re getting external moisture.” So those glands atrophy. Shrink. Shut down.
  </p>

  <p className="mb-4" data-id="element-437">
    Once a gland stays blocked for <strong data-id="element-438">six months or longer</strong>, the tissue dies. Permanently.
  </p>

  <p className="mb-4" data-id="element-439">
    You were born with roughly <strong data-id="element-440">75 glands per eyelid.</strong> By age 50, the average person has lost <strong data-id="element-441">30–40%</strong> of them.
    Heavy drop users? <strong data-id="element-442">Up to 60% are gone</strong> and they’re never coming back.
  </p>

  <p className="mb-4 font-semibold text-teal-600" data-id="element-443">But here’s what they also didn’t tell you:</p>

  <p className="mb-4" data-id="element-444">
    There’s a narrow window where you can still <strong data-id="element-445">save the glands you have left.</strong>
  </p>

  <p className="mb-4" data-id="element-446">
    Not with more drops. Not with another prescription.
  </p>

  <p className="mb-4" data-id="element-447">
    With the same technology ophthalmologists have been using in <strong data-id="element-448">$1,500 in-office treatments</strong> since 2011 
    <span className="text-teal-600 font-semibold" data-id="element-449"> therapeutic heat at exactly 42°C</span>, combined with gentle compression to manually open blocked glands.
  </p>

  <p className="mb-4" data-id="element-450">
    It’s called <strong className="text-teal-600" data-id="element-451">LipiFlow®</strong> & until recently, it was only available in medical offices,
    could cost over <strong data-id="element-452">$1,500 per session</strong>, and insurance rarely covered it.
  </p>
          </div>
          </div>
        </div>
      </div>
    </div>;
}